// ---------------------------------------------------------------------------
// useAuth.js  — single source of truth for client-side auth state.
//
// Reads (in priority order):
//   1.  sessionStorage["permissions"] — the full EffectiveAccessBundle
//       written by the login thunk (response.data.access). This is the
//       canonical shape after Phase 3.
//   2.  sessionStorage["authUser"]    — the legacy top-level fields the
//       backend still sends for compatibility (uid, role, countryCode,
//       isItAdmin, isHR, ...). Used as a fallback if the bundle is
//       missing (e.g. session created against an older API build).
//
// Every flag the legacy dashboard exposed (superUser, salvie, sheryl,
// babu, MPC, isShipper, TaskAuditor, maintenanceTeam, betaTester,
// snSignatory, isProjectApprovalCommittee, isFlOperations) is surfaced
// here under the same name (camelCase) so callers can do:
//
//     const { isSalvie, isManager, canViewIndia, hasAccess } = useAuth();
//     if (isSalvie || hasAccess('Documents Ready for Release')) { ... }
//
// IMPORTANT — backward compatibility:
//   • `isItAdmin`, `isSuperUser`, `isManager`, `isHR`, `isMPC`,
//     `roleName`, `empId`, `countryCode`, `isIndia`, `token` — these
//     names match the previous useAuth contract; existing pages keep
//     working.
//   • New flags + helpers are additive. Pages can adopt them gradually.
//
// HELPERS:
//   • hasAccess(permName)        — case-insensitive permission lookup
//   • hasRole(roleName)          — checks role claim
//   • canAccessModule(moduleKey) — reads the server-resolved Modules map
//   • canViewCountry(code)       — true if user can switch to that country
// ---------------------------------------------------------------------------

const BUNDLE_KEY = "permissions"; // stores the full EffectiveAccessBundle
const AUTH_KEY = "authUser";    // stores the legacy login response

/** Safe sessionStorage JSON read; returns null on any error. */
function readJSON(key) {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

/** Coerce any of "true" / true / 1 / "1" to true; otherwise false. */
function asBool(v) {
    if (v === true || v === 1) return true;
    if (typeof v === "string") {
        const s = v.toLowerCase();
        return s === "true" || s === "1";
    }
    return false;
}

const useAuth = () => {
    // ── pull both caches ────────────────────────────────────────────
    const bundleRaw = readJSON(BUNDLE_KEY);

    // The login thunk used to write JUST the permissions ARRAY into
    // sessionStorage["permissions"]. Phase 3 writes the FULL bundle
    // object there instead. Detect the legacy shape and treat it as
    // "bundle missing — fall back to authUser".
    const bundle = (bundleRaw && !Array.isArray(bundleRaw) && typeof bundleRaw === "object")
        ? bundleRaw
        : null;

    const authRaw = readJSON(AUTH_KEY) ?? {};
    const data = authRaw?.data ?? authRaw ?? {};

    // The login response itself includes an `access` sub-object that's
    // identical to the bundle. Prefer it if the dedicated bundle cache
    // wasn't written for any reason.
    const access = bundle ?? data?.access ?? null;

    // ── identity (works regardless of which cache is present) ───────
    const empId = access?.empId ?? data?.uid ?? data?.empId ?? "";
    const name = access?.name ?? data?.name ?? "";
    const roleName = access?.role ?? data?.role ?? "Employee";
    const roleId = access?.roleId ?? null;
    const department = access?.department ?? data?.department ?? data?.dept ?? "";
    const country = access?.country ?? data?.countryCode ?? data?.country ?? "USA";
    const companyCode = access?.companyCode ?? (country === "INDIA" ? "SN" : "SSW");
    const supervisorId = access?.supervisorId ?? data?.supervisorId ?? "";
    const epoLimit = access?.epoLimit ?? null;
    const token = data?.token ?? "";

    // ── permission list + lookup map ────────────────────────────────
    // The bundle stores `permissions` as a sorted string array. We
    // build a lowercased Set once so hasAccess() is O(1) and case-
    // insensitive (matches backend behavior).
    const permsArr =
        Array.isArray(access?.permissions) ? access.permissions
            : Array.isArray(bundleRaw) ? bundleRaw   // legacy: array-only cache
                : [];
    const permsSet = new Set(
        permsArr.filter((p) => typeof p === "string").map((p) => p.toLowerCase())
    );

    /** Case-insensitive permission check.  hasAccess('HR Menu') === true */
    const hasAccess = (perm) =>
        !!perm && permsSet.has(String(perm).toLowerCase());

    // ── module-visibility map (server-resolved) ─────────────────────
    const modules = access?.modules ?? {};
    /** canAccessModule('epo.accounting') — keys defined by EffectiveAccessService. */
    const canAccessModule = (key) =>
        !!key && modules[key] === true;

    // ── role flags ──────────────────────────────────────────────────
    // Priority: bundle (server-resolved) → JWT-claim string → role name match.
    // Boolean *string* claims come from the JWT via `data?.isXyz === "true"`.
    const flag = (bundleKey, claimKey, fallback = false) => {
        if (access && bundleKey in access) return !!access[bundleKey];
        if (claimKey && claimKey in data) return asBool(data[claimKey]);
        return fallback;
    };

    const isActualManager = flag("isActualManager", "isActualManager");
    const isPresident = flag("isPresident", "isPresident");
    const isItAdmin = flag("isItAdmin", "isItAdmin");
    const isSuperUser = flag("isSuperUser", "isSuperUser");
    const isManager = flag("isManager", "isManager");
    const isHR = flag("isHr", "isHR");
    const isFinance = flag("isFinance", "isFinance");
    const isDmsManager = flag("isDmsManager", "isDmsManager");
    const isSalvie = flag("isSalvie", "isSalvie");
    const isBabu = flag("isBabu", "isBabu");
    const isMPC = flag("isMpc", "isMPC");
    const isShipper = flag("isShipper", "isShipper");
    const isTaskAuditor = flag("isTaskAuditor", "isTaskAuditor");
    const isBetaTester = flag("isBetaTester", "isBetaTester");
    const isSnSignatory = flag("isSnSignatory", "isSnSignatory");
    const isProjectApprovalCommittee = flag("isProjectApprovalCommittee",
        "isProjectApprovalCommittee");
    const isFlOperations = flag("isFlOperations", "isFlOperations");
    const canViewIndia = flag("canViewIndia", "canViewIndia", country === "INDIA");
    const canViewUsa = flag("canViewUsa", "canViewUsa", country !== "INDIA");
    // An India account (a person of the India company): MAFIA Aux, Digital
    // Inspection, DMS and its tasks only. Decided by the server; an account
    // that was switched to India keeps its country, so this is NOT `isIndia`.
    const isIndiaAccount = flag("isIndiaAccount", "isIndiaAccount");

    /** True if the role NAME matches (e.g. "IT Admin", "HR Admin", "Manager"). */
    const hasRole = (r) =>
        !!r && (roleName === r
            || (Array.isArray(data?.roles) && data.roles.includes(r)));

    /** True if the user can switch to the given country context. */
    const canViewCountry = (code) => {
        if (!code) return false;
        const c = String(code).toUpperCase();
        if (c === "INDIA") return canViewIndia;
        if (c === "USA" || c === "JOSEPH") return canViewUsa;
        return false;
    };

    return {
        // identity
        empId,
        name,
        roleName,
        roleId,
        department,
        country,
        countryCode: country,         // legacy alias
        companyCode,
        supervisorId,
        epoLimit,
        token,
        isIndia: country === "INDIA",
        isIndiaAccount,

        // legacy boolean flags (every legacy GlobalVar boolean)
        isActualManager,
        isPresident,
        isItAdmin,
        isSuperUser,
        isManager,
        isHR,
        isFinance,
        isDmsManager,
        isSalvie,
        isBabu,
        isMPC,
        isShipper,
        isTaskAuditor,
        isBetaTester,
        isSnSignatory,
        isProjectApprovalCommittee,
        isFlOperations,
        canViewIndia,
        canViewUsa,

        // raw data so consumers can do auth.permissions / auth.modules
        permissions: permsArr,
        modules,

        // helpers
        hasAccess,
        hasRole,
        canAccessModule,
        canViewCountry,
    };
};

export default useAuth;
